import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { api, isValidEmail } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export const AuthShell = ({ title, children }) => (
  <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-12">
    <Link to="/" className="mb-8 flex justify-center">
      <img src="/woningalert-logo.png" alt="WoningAlert" className="h-10 w-auto" />
    </Link>
    <div className="rounded-3xl border border-border bg-white p-8 shadow-sm">
      <h1 className="font-display text-2xl font-bold text-navy">{title}</h1>
      <div className="mt-6">{children}</div>
    </div>
  </div>
);

const Login = () => {
  const { t } = useT();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidEmail(email)) return setError(t("auth.err.email"));
    setBusy(true);
    try {
      await login(email, password);
      navigate(location.state?.from || "/dashboard");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("auth.login.title")}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-sm font-semibold text-navy">{t("common.email")}</Label>
          <Input className="mt-2 rounded-xl" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        </div>
        <div>
          <Label className="text-sm font-semibold text-navy">{t("common.password")}</Label>
          <div className="relative mt-2">
            <Input
              className="rounded-xl pr-10"
              data-testid="login-password"
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div className="text-right">
          <Link to="/wachtwoord-vergeten" className="text-sm font-medium text-brandblue">
            {t("auth.login.forgot")}
          </Link>
        </div>
        {error && <p className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full rounded-xl bg-brandblue hover:bg-brandblue/90">
          {busy ? <Loader2 className="animate-spin" size={18} /> : t("auth.login.submit")}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        {t("auth.login.noAccount")}{" "}
        <Link to="/register" className="font-semibold text-brandblue">
          {t("nav.register")}
        </Link>
      </p>
    </AuthShell>
  );
};

export default Login;
